import { useQuery } from "@tanstack/react-query";
import { FileX } from "lucide-react";
import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { RequestedDocument } from "@recobra/schemas/document";
import { requestedDocumentRequirementKey, type Task } from "@recobra/schemas/task";
import { DOCUMENT_COLLECTION_SYSTEM_TASKS } from "@recobra/schemas/task-catalog";
import { Field, FieldLabel } from "@recobra/ui/field";
import { Table, TableBody, TableCell, TableRow } from "@recobra/ui/table";

import { PageErrorAlert } from "../../lib";
import { tasksApi } from "../../lib/api";

/** Documents ruled nonexistent on a completed document-collection task for this case. */
export const TaskCaseExceptionDocuments: FC<{ task: Task }> = ({ task }) => {
  const { data, error } = useQuery({
    queryKey: ["tasks", "case-document-collection", task.contextId],
    queryFn: () =>
      tasksApi.listAllTasks({
        contextType: "case",
        contextId: task.contextId,
        status: "done",
        system: DOCUMENT_COLLECTION_SYSTEM_TASKS,
      }),
  });

  if (error) return <PageErrorAlert error={error} />;

  const nonexistentDocuments = (data ?? []).flatMap((collectionTask) =>
    collectionTask.requestedDocuments
      .filter(
        (document) =>
          document.required !== false &&
          requestedDocumentRequirementKey(document) in collectionTask.requirementProgress.waived,
      )
      .map((document) => ({ taskId: collectionTask.id, document })),
  );

  if (nonexistentDocuments.length === 0) return null;

  return (
    <Field>
      <FieldLabel>{m.task_field_nonexistent_documents()}</FieldLabel>
      <Table>
        <TableBody>
          {nonexistentDocuments.map(({ taskId, document }) => (
            <RequestedDocumentRow key={`${taskId}-${document.key}`} document={document} />
          ))}
        </TableBody>
      </Table>
    </Field>
  );
};

const RequestedDocumentRow: FC<{ document: RequestedDocument }> = ({ document }) => (
  <TableRow className="hover:bg-transparent">
    <TableCell className="flex items-center gap-2 px-0 py-1.5">
      <FileX className="size-4 shrink-0 text-destructive" />
      {m.document_type_label({ documentType: document.documentType })}
      {document.label ? <span className="text-muted-foreground">({document.label})</span> : null}
    </TableCell>
  </TableRow>
);
